import * as React from "react";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { AdviceGivenIncludingPersonalRecommendationQuestionText } from "../../Resource/Contents";
import { SectionHeader } from "../../SharedComponents";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { InvestmentPathwayWarningMessages, PensionPathwayInvestmentOptionArray, PensionPathwayInvestmentOptionArrayMap } from "./Resource/Contents";
import { InvestmentPathOptions, investmentPathOptionsArray, investmentPathOptionsMap } from "../../../../../../Shared/IST/Resource/Content";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { ProcessType } from "../../../../../../../Wrap/Constants/Enums/Client/DashboardFilter/ProcessType";
import { InvestmentPathwayAnswers } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/InvestmentPathwayAnswers";
import { PensionPathwayInvestmentOption } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/PensionPathwayInvestmentOption";
import { string } from "prop-types";
import { Lightbox } from "../../../../../../../react-core/Html/Components/Lightbox";
import { User } from "../../../../../../../Wrap/Models/Models/User/User";
import { UserStore } from "../../../../../Stroes/Variable";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";

interface IInvestmentPathSelectionProps extends IProcessComponentProps {
    data: InvestmentPathwayAnswers;
    hardBlockforAdviserUser?: boolean;
    isFirstCrystallisation?: boolean;
    activityType?: ProcessType;
    hasOuterRingAssets?: boolean;
    toggleSwitchInvestments?(value:boolean) : any;
    modelData?: DrawdownModel;

}

interface IInvestmentPathSelectionState {
    userInfo: User;
    showCOBSWarningMessage: boolean;
    CashWarningConfirmation: boolean;
}

export class InvestmentPathSelection extends ProcessComponent<IInvestmentPathSelectionProps, IInvestmentPathSelectionState> {
    constructor(props: IInvestmentPathSelectionProps) {
        super(props);
        this.state = {
            userInfo: UserStore.getUserInfo(),
            showCOBSWarningMessage: false,
            CashWarningConfirmation: false,
        };
    }

    checkSwitchInvestments(){
        if (this.props.toggleSwitchInvestments) {
            const isToogle = !this.props.data?.PersonalRecommendation && this.props.data?.InvestmentOption == PensionPathwayInvestmentOption.SelectInvestments;
            this.props.toggleSwitchInvestments(isToogle);
        }
    }

    private pathwaySettingsConfig = (SelectedPathwayId) => {

        const selectedPathway= this.props.data?.InvestmentPathwaysSettingsForSettings?.find(
            (setting) => setting.InvestmentPathwayQuestionSettingOptionId === SelectedPathwayId
        );
        return [
            {
                title: <strong>Selected Investment Pathway:</strong>,
                data: selectedPathway?.CurrentPathwayInvestmentText || null
            }
        ];
    }; 

    private checkAmountGreaterThanFiftyPer(){
        const crystallisationDetails = this.props.modelData?.CrystallisationDetails;
        if(crystallisationDetails?.AvailableCash?.Amount != null && crystallisationDetails?.AvailableToCrystallise?.Amount){
            return crystallisationDetails?.AvailableCash?.Amount > 0.5 * crystallisationDetails?.AvailableToCrystallise?.Amount;
        }
        return false;
    }

    public render(): JSX.Element {
        const { data , hardBlockforAdviserUser, isFirstCrystallisation } = this.props;
        return (
            <>
                {SectionHeader("Financial advice")}
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={AdviceGivenIncludingPersonalRecommendationQuestionText}
                    selected={data?.PersonalRecommendation}
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    onChange={(v) => {
                        this.onInvestPathwayChange({ PersonalRecommendation: v })
                        if(this.state.userInfo?.IsAdviser && !hardBlockforAdviserUser){
                            this.setState({ showCOBSWarningMessage: !v });
                        }
                    }}
                    required={"Please select"}
                    validationRules={[
                        {
                            errorMessage: "To continue, you must have given financial advice.",
                            validator: (v) => {
                                if (this.state.userInfo?.IsInternalUser) {
                                    return true;
                                }

                                if (isFirstCrystallisation) {
                                    return v;
                                }
                            
                                if (hardBlockforAdviserUser) {
                                    return data?.PersonalRecommendation || false; 
                                }
                            
                                return true; 
                            }
                        },
                    ]}  
                />
                {this.state.showCOBSWarningMessage && this.renderWarningMessage()}
                {// TODO: Organize sections below in hierarchy
                }
                {!(data?.PersonalRecommendation ?? true) && !hardBlockforAdviserUser &&
                    <SelectEditor
                        cssWrapper="rg-inline mb-16"
                        label={"How does the client wish to proceed with the assets in the pension?"}
                        selected={data?.InvestmentOption}
                        options={PensionPathwayInvestmentOptionArray}
                        getText={(o) => PensionPathwayInvestmentOptionArrayMap[o]}
                        onChange={(v) => this.onInvestPathwayChange({ InvestmentOption: v })}
                        required={"Please select"}
                        validationRules={[
                            {
                                errorMessage: "you must choose one investment option to continue",
                                validator: InvestmentPathSelection.validationInvestmentOptionIsSpecified,
                            },
                        ]}
                    />}

                {!(data?.PersonalRecommendation ?? true) && data?.InvestmentOption == PensionPathwayInvestmentOption.InvestmentPathway &&
                    <SelectEditor
                        cssWrapper="rg-inline mb-16"
                        label={"Which of the below best reflects your investment aims:"}
                        selected={data?.SelectedPathwayId}
                        options={investmentPathOptionsArray}
                        getText={(o) => investmentPathOptionsMap[o]}
                        onChange={(v) => {
                            this.onInvestPathwayChange({ SelectedPathwayId: v })
                        }}
                        required={"Please select the one which best reflects your investment aims"}
                        validationRules={[
                            {
                                errorMessage: "you must choose one to continue",
                                validator: InvestmentPathSelection.validateInvestmentPathwayOptionIsSpecified,
                            },
                        ]}
                    />}

                {
                    data?.SelectedPathwayId ? <Typography type="common" config={this.pathwaySettingsConfig(data.SelectedPathwayId)} />
                    : null
                }

                {!(data?.PersonalRecommendation ?? true) && data?.InvestmentOption == PensionPathwayInvestmentOption.InvestmentPathway &&
                    <>
                        <RadiosEditor
                            cssWrapper="rg-inline mb-16"
                            label="Take up this investment pathway choice?  (Yes/No)"
                            selected={data?.TakeUpInvestmentPathwayChoice}
                            options={[true, false]}
                            getText={(v) => (v ? "Yes" : "No")}
                            onChange={(v) => this.onInvestPathwayChange({ TakeUpInvestmentPathwayChoice: v })}
                            required={"Please select"}
                            validationRules={[
                                {errorMessage: "Please specify how do you wish to proceed.",
                                    validator: (v) => v != null
                                }
                            ]}
                        />

                        {!data?.TakeUpInvestmentPathwayChoice &&
                        <CheckboxEditor
                            cssWrapper={"square-checkbox"}
                            label="Please confirm the client wishes to proceed on the basis of remaining invested in the current allocation."
                            value={data?.ProceedWithRemainingsInCurrent}
                            onChange={(v) => this.onInvestPathwayChange({ ProceedWithRemainingsInCurrent: v })}
                            validationRules={[
                                {
                                    errorMessage: "Please provide confirmation",
                                    validator: (v) => data.TakeUpInvestmentPathwayChoice || v
                                }
                            ]}
                        />}
                        {this.checkAmountGreaterThanFiftyPer() &&
                        <CheckboxEditor
                            cssWrapper={"square-checkbox"}
                            label="The pension is allocated in more than 50% of cash like investments (either directly cash or an asset with a high cash weighting) and the investments are at risk of inflation erosion I confirm the client wishes to proceed on this basis"
                            value={this.state.CashWarningConfirmation}
                            onChange={(v) => {
                                this.setState({ CashWarningConfirmation: v });
                            }}
                            validationRules={[
                                {
                                    errorMessage: "Please provide confirmation",
                                    validator: (v) => v
                                }
                            ]}
                        />}
                        {(data?.TakeUpInvestmentPathwayChoice ?? false) && 
                        <Alert
                            alertType={AlertType.Warning}
                            message={this.getWarningMessageOnTakingUpInvestmetnPathwayChoice()}
                        />}
                    </>
                }

            </>
        );
    }

    private onInvestPathwayChange(v) {
        const { data, onChange } = this.props;
        var investmentPathwayAnswers: InvestmentPathwayAnswers = data ? JSON.parse(JSON.stringify(data)) : { PersonalRecommendation: true };

        investmentPathwayAnswers.PersonalRecommendation = v.PersonalRecommendation != undefined ? v.PersonalRecommendation : investmentPathwayAnswers.PersonalRecommendation
        investmentPathwayAnswers.InvestmentOption = v.InvestmentOption ?? investmentPathwayAnswers.InvestmentOption;
        investmentPathwayAnswers.SelectedPathwayId = v.SelectedPathwayId ?? investmentPathwayAnswers.SelectedPathwayId;
        investmentPathwayAnswers.TakeUpInvestmentPathwayChoice = v.TakeUpInvestmentPathwayChoice ?? investmentPathwayAnswers.TakeUpInvestmentPathwayChoice;
        investmentPathwayAnswers.ProceedWithRemainingsInCurrent = v.ProceedWithRemainingsInCurrent ?? investmentPathwayAnswers.ProceedWithRemainingsInCurrent;

        if (investmentPathwayAnswers.SelectedPathwayId != null) {
            investmentPathwayAnswers.LinkedProduct = investmentPathwayAnswers.InvestmentPathwaysSettingsForSettings.find(
                (setting) => setting.InvestmentPathwayQuestionSettingOptionId === investmentPathwayAnswers.SelectedPathwayId).SelectedInstrumentId;
        }

        if (investmentPathwayAnswers.TakeUpInvestmentPathwayChoice)
        {
            investmentPathwayAnswers.ProceedWithRemainingsInCurrent = null;
        }

        if (investmentPathwayAnswers.InvestmentOption != PensionPathwayInvestmentOption.InvestmentPathway) {
            investmentPathwayAnswers.SelectedPathwayId = null;
            investmentPathwayAnswers.TakeUpInvestmentPathwayChoice = null;
            investmentPathwayAnswers.ProceedWithRemainingsInCurrent = null;
            investmentPathwayAnswers.LinkedProduct = null;
        }

        if (investmentPathwayAnswers.PersonalRecommendation == true) {
            investmentPathwayAnswers.InvestmentOption = null;
            investmentPathwayAnswers.SelectedPathwayId = null;
            investmentPathwayAnswers.TakeUpInvestmentPathwayChoice = null;
            investmentPathwayAnswers.ProceedWithRemainingsInCurrent = null;
            investmentPathwayAnswers.LinkedProduct = null;
        }


        onChange(investmentPathwayAnswers);
        this.checkSwitchInvestments();

    }

    private static validationInvestmentOptionIsSpecified(v: number): boolean {
        return v != PensionPathwayInvestmentOption.NotSpecified;
    }

    private static validateInvestmentPathwayOptionIsSpecified(v: number): boolean {
        return v != InvestmentPathOptions.NotSpecified;
    }

    private getWarningMessageOnTakingUpInvestmetnPathwayChoice(): string {
        const { activityType, hasOuterRingAssets } = this.props;

        switch(activityType) {
            case ProcessType.NewBusiness:
                return InvestmentPathwayWarningMessages.NewBusiness;
            case ProcessType.TakePensionBenefit:
                return hasOuterRingAssets ? 
                    InvestmentPathwayWarningMessages.TPBWithOuterRing : 
                    InvestmentPathwayWarningMessages.TPBNoOuterRing;
            case ProcessType.Topup:
                return hasOuterRingAssets ? 
                    InvestmentPathwayWarningMessages.TopUpWithITFCWithOuterRing : 
                    InvestmentPathwayWarningMessages.TopUpWithITFCNoOuterRing;
            case ProcessType.WhatIfTool:
                return InvestmentPathwayWarningMessages.WhatIf;
            default:
                return "";
        }
    }

    private renderWarningMessage() {
        return (
            <Lightbox
                unclosable={true}
                noOverlay={false}
                noAutofocus={true}
                cssWrapper="is-default-lightbox" title={""} 
                show={this.state.showCOBSWarningMessage} 
                onClose={() => this.setState({ showCOBSWarningMessage: false })}           >
                <p>I confirm I have met my COBS 19.7 requirements on the provision of retirement risk warnings, where appropriate.</p>
                <button
                    className="btn primary"
                    onClick={() => this.setState({ showCOBSWarningMessage: false })}
                >
                    OK
                </button>
            </Lightbox>
        );
    }
}
